/**
 * One suggestion from Claude: a judge flag or a review. Accept makes the flag or review
 * in the reviewer's name; a judge flag is added to any flag the result already has. Reject
 * takes an optional reason, which Claude reads before it suggests again.
 */
import { Bot } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import Button from '../../../../components/Button';
import InlineErrorDisplay from '../../../../components/InlineErrorDisplay';
import type { EvalSuggestion } from '../../types/evalTypes';
import { formatDateTime } from '../../utils/evalFormat';

const MAX_REASON_CHARS = 2000;

interface SuggestionCardProps {
  suggestion: EvalSuggestion;
  currentUid: string | undefined;
  /** Shown under the title, e.g. where the result is and a button to show it. */
  context?: ReactNode;
  /** Throws on failure. */
  onDecide: (accept: boolean, rejectReason?: string) => Promise<void>;
}

const title = (suggestion: EvalSuggestion): ReactNode =>
  suggestion.kind === 'review' ? (
    <>Review: {suggestion.decision === 'go' ? 'Go' : 'No-go'}</>
  ) : (
    <>
      Judge wrong on: <span className="font-mono">{suggestion.checkIds.join(', ')}</span>
    </>
  );

export const SuggestionCard = ({ suggestion, currentUid, context, onDecide }: SuggestionCardProps) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (accept: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await onDecide(accept, accept ? undefined : reason.trim());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to save the decision.');
      setSaving(false);
    }
  };

  const by = (uid: string | null) => (uid === currentUid ? 'you' : uid);

  return (
    <div className="text-xs bg-violet-50 border border-violet-200 rounded p-2 space-y-1">
      <div className="flex items-start justify-between gap-2">
        <span className="inline-flex items-center gap-1 font-medium text-violet-900">
          <Bot size={12} /> Claude suggests · {title(suggestion)}
        </span>
        {suggestion.status !== 'pending' && (
          <span
            className={`shrink-0 px-2 py-0.5 rounded-full ${suggestion.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}
          >
            {suggestion.status === 'accepted' ? 'Accepted' : 'Rejected'}
          </span>
        )}
      </div>
      {context}
      <p className="whitespace-pre-wrap text-gray-800">{suggestion.note}</p>
      <p className="text-gray-500">
        Token "{suggestion.tokenName}", {formatDateTime(suggestion.createdAt)}
        {suggestion.decidedAt !== null && ` · ${suggestion.status} by ${by(suggestion.decidedBy)}, ${formatDateTime(suggestion.decidedAt)}`}
      </p>
      {suggestion.rejectReason && <p className="whitespace-pre-wrap text-gray-700">Reason: {suggestion.rejectReason}</p>}

      {suggestion.status === 'pending' && rejecting && (
        <textarea
          value={reason}
          maxLength={MAX_REASON_CHARS}
          onChange={(event) => setReason(event.target.value)}
          disabled={saving}
          rows={2}
          placeholder="Why not? Optional. Claude reads it before it suggests again."
          className="w-full px-2 py-1 border border-gray-300 rounded text-xs resize-y"
        />
      )}
      {error && <InlineErrorDisplay message={error} onDismiss={() => setError(null)} />}
      {suggestion.status === 'pending' && (
        <div className="flex flex-wrap justify-end gap-2 pt-1">
          {rejecting ? (
            <>
              <Button variant="outline" size="sm" onClick={() => setRejecting(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="danger" size="sm" onClick={() => void decide(false)} disabled={saving} loading={saving}>
                Reject
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setRejecting(true)} disabled={saving}>
                Reject…
              </Button>
              <Button variant="primary" size="sm" onClick={() => void decide(true)} disabled={saving} loading={saving}>
                Accept
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
